import { useQuery } from '@tanstack/react-query';

import { envApi } from '@/domain/env/api';
import { envKeys } from '@/domain/env/queries/keys';
import type { EndpointResponse } from '@/services/api/endpointTypes';
import { queryOptions } from '@/services/query/queryOptions';

type CompanyUserPreferencesCache = EndpointResponse<typeof envApi.getCompanyUserPreferences>;

// The company reaches the request only in its CompanyId header, which names the company in scope of the router
// state when the request starts. Key it by that company, not the URL's: in the render where the two differ, one
// company's answer would be cached under the other.
export const companyUserPreferencesQuery = (companyId: string) =>
  queryOptions({
    queryKey: envKeys.companyUserPreferences(companyId),
    queryFn: () => envApi.getCompanyUserPreferences(),
    // No refetch on window focus or on a tab's mount: it could change access mid-page and fire a section redirect.
    // Only the company in scope is kept: the entry goes as soon as nothing observes it.
    staleTime: Infinity,
    cacheTime: 0,
  });

export const useCompanyUserPreferences = <TData = CompanyUserPreferencesCache>(
  companyId: string | null,
  { select }: { select?: (preferences: CompanyUserPreferencesCache) => TData } = {},
) => useQuery({ ...companyUserPreferencesQuery(companyId ?? ''), enabled: Boolean(companyId), select });
